import { isCartazFormat } from "../../../services/Utils";

/**
 *  Processes the products with the theme styles, applying the main styles to all products and the secondary styles to the products that should be highlighted (products that expand on row or column direction).
 * @param {*} products The products to be processed
 * @param {*} theme The theme with the styles to be applied to the products
 * @param {*} setLoading A function to set the loading state
 * @returns The processed products with the applied theme styles
 */
export function processProductsWithThemeStyles(
  products,
  theme,
  setLoading,
  format,
) {
  if (isCartazFormat(format)) return products; // cartaz format doesn't follow the same style rules, so we skip the processing
  setLoading(true);

  const globalMaxSpan = getMaxSpanInGrid(products);

  const mainBgColor = theme?.bgMainColor || "#f8a617";
  const mainTextColor = theme?.textMainColor || "#ffffff";
  const secondaryTextColor = theme?.textSecondaryColor || "#000000";
  const secondaryBgColor = theme?.bgSecondaryColor || "#991B1B";
  const mainPriceTextColor = theme?.mainPriceTextColor || "#ffffff";
  const secondaryPriceTextColor = theme?.secondaryPriceTextColor || "#ffffff";
  const processedProducts = products.map((p) => {
    if (isTargetProduct(p, globalMaxSpan)) {
      p.styles.bgColor = secondaryBgColor;
      p.styles.secondaryBgColor = mainBgColor;
      p.styles.txtColor = secondaryTextColor;
      p.styles.secondaryTxtColor = mainTextColor;
      p.styles.priceTxtColor = secondaryPriceTextColor;
      p.styles.secondaryPriceTxtColor = mainPriceTextColor;
      p.styles.priceStyleTmp = theme?.secondaryPrice?.displayUrl;
      p.styles.secondaryPriceStyleTmp = theme?.mainPrice?.displayUrl;
      p.styles.priceStyle = theme?.secondaryPrice?.location;
      p.styles.secondaryPriceStyle = theme?.mainPrice?.location;
    } else {
      p.styles.bgColor = mainBgColor;
      p.styles.secondaryBgColor = secondaryBgColor;
      p.styles.txtColor = mainTextColor;
      p.styles.secondaryTxtColor = secondaryTextColor;
      p.styles.priceTxtColor = mainPriceTextColor;
      p.styles.secondaryPriceTxtColor = secondaryPriceTextColor;
      p.styles.priceStyleTmp = theme?.mainPrice?.displayUrl;
      p.styles.secondaryPriceStyleTmp = theme?.secondaryPrice?.displayUrl;
      p.styles.priceStyle = theme?.mainPrice?.location;
      p.styles.secondaryPriceStyle = theme?.secondaryPrice?.location;
    }
    return p;
  });

  setLoading(false);
  return processedProducts;
}
/**
 * Calculates the numeric span of a product's grid property (e.g., "1/3" returns 2).
 * @param {string} gridProperty - The CSS grid string (column or row).
 * @returns {number} The integer span.
 */
function getSpan(gridProperty) {
  const parts = gridProperty.split("/");
  return Math.abs(parseInt(parts[1]) - parseInt(parts[0]));
}

/**
 * Calculates max row/column spans and returns which direction dominates.
 * direction: "row" | "column" | "both" | "none"
 */
export function getMaxSpanDirection(products) {
  if (!products.length) {
    return {
      direction: "none",
      maxSpan: 0,
      maxRowSpan: 0,
      maxColumnSpan: 0,
    };
  }

  const metrics = products.reduce(
    (acc, p) => {
      const colSpan = getSpan(p.styles.gridColumn);
      const rowSpan = getSpan(p.styles.gridRow);

      return {
        maxRowSpan: Math.max(acc.maxRowSpan, rowSpan),
        maxColumnSpan: Math.max(acc.maxColumnSpan, colSpan),
      };
    },
    { maxRowSpan: 0, maxColumnSpan: 0 },
  );

  const { maxRowSpan, maxColumnSpan } = metrics;
  const maxSpan = Math.max(maxRowSpan, maxColumnSpan);

  let direction = "both";
  if (maxSpan === 0) direction = "none";
  else if (maxRowSpan > maxColumnSpan) direction = "row";
  else if (maxColumnSpan > maxRowSpan) direction = "column";

  return {
    direction,
    maxSpan,
    maxRowSpan,
    maxColumnSpan,
  };
}

/**
 * Calculates the maximum span (row or column) present in the entire product list.
 * @param {Array} products
 * @returns {number} The largest span found.
 */
function getMaxSpanInGrid(products) {
  return getMaxSpanDirection(products).maxSpan;
}

/**
 * Checks if a specific product matches the pre-calculated max span.
 */
function isTargetProduct(product, maxSpan) {
  const productMax = Math.max(
    getSpan(product.styles.gridColumn),
    getSpan(product.styles.gridRow),
  );
  return productMax === maxSpan && maxSpan > 1;
}
